import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { ScoreDisplay } from '../../../voice-agent/components/ScoreDisplay';
import type { Category, ScenarioWithCompletion, SkillArea } from '../../../voice-agent/types/scenarios';
import { SkillAreaProgress } from './SkillAreaProgress';
import { styleSheet } from './styles';

interface SkillAreaWithScenarios {
	skillArea: SkillArea;
	scenarios: ScenarioWithCompletion[];
}

interface CategoryProgressProps {
	category: Category;
	skillAreasWithScenarios: SkillAreaWithScenarios[];
	onScenarioClick: (scenario: ScenarioWithCompletion) => void;
}

/**
 * CategoryProgress displays a collapsible section for a category
 * containing all its skill areas and their scenarios
 */
export const CategoryProgress: React.FC<CategoryProgressProps> = ({
	category,
	skillAreasWithScenarios,
	onScenarioClick,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	// Calculate category-level stats
	const allScenarios = skillAreasWithScenarios.flatMap(sa => sa.scenarios);
	const practicedScenarios = allScenarios.filter(s => s.attemptCount && s.attemptCount > 0);
	const totalScenarios = allScenarios.length;
	const averageScore =
		practicedScenarios.length > 0
			? practicedScenarios
					.filter(s => s.bestScore !== undefined && s.bestScore !== null)
					.reduce((sum, s) => sum + (s.bestScore || 0), 0) / practicedScenarios.length
			: null;

	return (
		<div className={css(styleSheet.categorySection)}>
			<div
				className={css(styleSheet.categoryHeader, isExpanded && styleSheet.categoryHeaderExpanded)}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<svg className={css(styleSheet.expandIcon, isExpanded && styleSheet.expandIconRotated)} viewBox='0 0 24 24'>
					<path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' fill='currentColor' />
				</svg>
				<div className={css(styleSheet.categoryInfo)}>
					<div className={css(styleSheet.categoryName)}>
						{category.name}
						{category.role && <span className={css(styleSheet.categoryRoleBadge)}>{category.role}</span>}
					</div>
					<div className={css(styleSheet.categoryDescription)}>{category.description}</div>
				</div>
				<div className={css(styleSheet.categoryStats)}>
					<div className={css(styleSheet.categoryStat)}>
						<div className={css(styleSheet.categoryStatLabel)}>Progress</div>
						<div className={css(styleSheet.categoryStatValue)}>
							{practicedScenarios.length}/{totalScenarios}
						</div>
					</div>
					{averageScore !== null && (
						<div className={css(styleSheet.categoryStat)}>
							<div className={css(styleSheet.categoryStatLabel)}>Avg Score</div>
							<div className={css(styleSheet.categoryStatValue)}>
								<ScoreDisplay score={averageScore} size='small' />
							</div>
						</div>
					)}
				</div>
			</div>

			{isExpanded && (
				<div className={css(styleSheet.skillAreasInCategory)}>
					{skillAreasWithScenarios.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>No skill areas in this category</div>
					) : (
						skillAreasWithScenarios.map(({ skillArea, scenarios }) => (
							<SkillAreaProgress
								key={skillArea.id}
								skillArea={skillArea}
								scenarios={scenarios}
								onScenarioClick={onScenarioClick}
							/>
						))
					)}
				</div>
			)}
		</div>
	);
};
